/**
 * Copyright (c) 2025 Celestis AI
 *
 * WARNING: Unauthorized copying, modification, distribution, or use of this
 * software, via any medium, is strictly prohibited without explicit written
 * permission from Celestis AI.
 *
 * All rights reserved.
 */

import { useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle } from "lucide-react";
import celestisLogo from '@/assets/celestis-logo-new.png';
import { generateStarfieldStars } from "@/utils/starfield";

interface ConfigurationErrorScreenProps {
  errorMessage?: string;
}

export const ConfigurationErrorScreen = ({ errorMessage }: ConfigurationErrorScreenProps) => {
  // Generate star positions using crypto.getRandomValues() to avoid Math.random() security issues
  const stars = useMemo(() => generateStarfieldStars(50), []);

  return (
    <div className="min-h-screen min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900 p-4 relative">
      {/* Starfield background matching pitch deck slides */}
      <div className="absolute inset-0 overflow-hidden opacity-30">
        {stars.map((star) => (
          <div
            key={`${star.left}-${star.top}-${star.animationDelay}`}
            className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{
              left: star.left,
              top: star.top,
              animationDelay: star.animationDelay,
              animationDuration: star.animationDuration,
            }}
          />
        ))}
      </div>
      <Card className="w-full max-w-lg mx-auto bg-slate-950/80 border-2 border-purple-400/30 backdrop-blur-sm relative z-10">
        <CardHeader className="text-center space-y-6 pb-8">
          <div className="flex items-center justify-center">
            <img src={celestisLogo} alt="Celestis Logo" className="w-48 h-48 object-contain" />
          </div>
          <div className="flex items-center justify-center">
            <AlertCircle className="h-12 w-12 text-yellow-500" />
          </div>
          <CardTitle className="text-white text-2xl">Configuration Error</CardTitle>
          <CardDescription className="text-gray-300 text-base">
            {errorMessage || "The application is not properly configured. Please contact support for assistance."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 px-8 pb-8">
          <div className="text-center text-sm text-gray-400">
            Please refresh the page or contact support if this issue persists.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

